import logoImg from 'src/public/img/mh-logo-text.svg';
import { Trans, useTranslation } from 'react-i18next';
import {
    BodyTextMedium,
    Form,
    FormInputSelect,
    useForm,
    HeadingText50,
} from '@modernhealth/vital-components';
import React, { useEffect, useState, useCallback } from 'react';
import styled from '@emotion/styled';
import { ExternalLink } from 'src/shared/components/ExternalLink';
import { useDispatch, useSelector } from 'react-redux';
import { getSortedCountries } from 'src/modules/countries/selectors/countrySelectors';
import { clickwrapService } from 'src/config/rootService';
import { FieldValues, useFieldArray, UseFormReturn } from 'react-hook-form';
import { Consents } from 'src/modules/auth/services/ClickwrapService';
import { config } from 'src/config/config';
import { ConsentCheckbox } from 'src/modules/auth/components/ConsentCheckbox';
import { getSortedLanguages } from 'src/modules/languages/selectors/languageSelectors';
import { Dispatch } from 'src/config/rootStore';
import {
    getLanguageNameByLocale,
    getPreferredLanguage,
} from 'src/shared/helpers/getPreferredLanguage';
import i18n from 'i18next';
import { Country } from 'src/modules/auth/screens/ClickwrapConsentForm';

const getDescriptionKeyForCountry = (country: Country): string => {
    const defaultKey = 'consent_form_page.clickwrap.description';

    const keys = {
        [Country.SOUTH_KOREA]: `consent_form_page.clickwrap.description.korea`,
    };

    return keys[country] ?? defaultKey;
};

interface ExtendedConsent extends Consents {
    acceptanceLanguage: string;
    isChecked: boolean;
}

interface ConsentForm extends FieldValues {
    language: string;
    country: Country;
    birthDate: string | null;
    consents: ExtendedConsent[];
}

export const LegalContractsPage = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch<Dispatch>();
    const [isFetchingConsents, setIsFetchingConsents] = useState(false);
    const languagesOptions = useSelector(getSortedLanguages);
    const countries = useSelector(getSortedCountries);
    const form = useForm<ConsentForm>({
        mode: 'onChange',
        defaultValues: {
            language: getLanguageNameByLocale(getPreferredLanguage()) || 'English',
            country: Country.US,
            consents: [],
        },
    });

    const { fields, replace } = useFieldArray({
        control: form.control,
        name: 'consents',
    });

    const fetchConsents = useCallback(async () => {
        form.setValue('consents', []);
        setIsFetchingConsents(true);
        const result = await clickwrapService.fetchConsents({
            country: form.getValues().country,
            language: form.getValues().language,
            skipCache: true,
        });

        await replace(
            result.contractsData.map(contract => ({
                ...contract,
                isChecked: false,
            })),
        );
        setIsFetchingConsents(false);
    }, [form, replace]);

    const updateLanguage = useCallback(
        async language => {
            await i18n.changeLanguage(getPreferredLanguage(language));
            dispatch.countries.fetchCountries();
        },
        [dispatch.countries],
    );

    useEffect(() => {
        dispatch.languages.fetchLanguages();
        dispatch.countries.fetchCountries();
        fetchConsents();
    }, [dispatch.countries, dispatch.languages, fetchConsents]);

    useEffect(() => {
        const subscription = form.watch(async (formValues, { name, type }) => {
            const selectedCountry = formValues.country;
            const selectedLanguage = formValues.language;

            if (type === 'change' && name === 'country' && selectedCountry) {
                form.setValue('consents', []);
                await fetchConsents();
            }

            if (type === 'change' && name === 'language' && selectedLanguage) {
                form.setValue('consents', []);

                await updateLanguage(selectedLanguage);
                await fetchConsents();
            }
        });
        return () => subscription.unsubscribe();
    }, [form, updateLanguage, fetchConsents]);

    return (
        <>
            <BrandImage
                src={logoImg}
                onClick={() => {
                    window.location.href = config.modernHealthURL;
                }}
            />
            <Container>
                <FormContainer definition={form as unknown as UseFormReturn<FieldValues>}>
                    <HeadingText50>{t('auth:registration_form.subtitle')}</HeadingText50>
                    <BodyTextMedium fontColor="gray65">
                        <Trans
                            i18nKey={getDescriptionKeyForCountry(form.getValues().country)}
                            ns="auth"
                            components={{
                                privacyNotice: (
                                    <SubtitleLink
                                        openInNewTab
                                        href={`${config.modernHealthURL}/privacy-notice`}
                                    />
                                ),
                                termOfUse: (
                                    <SubtitleLink
                                        openInNewTab
                                        href={`${config.modernHealthURL}/terms-of-use`}
                                    />
                                ),
                            }}
                        />
                    </BodyTextMedium>
                    <FormInputSelect
                        label={t('auth:consent_form_page.label.country_of_residence')}
                        name="country"
                        options={countries.map(({ text, value: optionValue }) => ({
                            value: optionValue,
                            label: text,
                        }))}
                    />
                    <FormInputSelect
                        label={t('dependents:form.label.language')}
                        name="language"
                        options={languagesOptions.map(({ text, value: optionValue }) => ({
                            value: optionValue,
                            label: text,
                        }))}
                    />
                    <ConsentCheckbox fetchingConsents={isFetchingConsents} consents={fields} />
                </FormContainer>
            </Container>
        </>
    );
};

const BrandImage = styled.img({
    marginLeft: 32,
    marginTop: 20,
    width: '15em',
    cursor: 'pointer',
    marginRight: 12,
});

const SubtitleLink = styled(ExternalLink)({
    fontSize: 'inherit',
});

const FormContainer = styled(Form)({
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    maxWidth: '500px',
    flexGrow: 1,
});

const Container = styled.div({
    display: 'flex',
    alignItems: 'start',
    paddingTop: '110px',
    justifyContent: 'center',
    flexGrow: 1,
});
